import { buildStatedFigureRows, type StatedFigure } from './statedFigures'
import { EditableValue } from './EditableValue'

/**
 * Each figure a broker stated, kept as stated, with the platform's own figure and
 * the variance beside it (Gate 0 D1). Nothing is chosen between them: the
 * stated figure stands, the calculation is shown for information. Renders
 * nothing when no figure has been stated.
 *
 * The platform never overwrites or back-solves the stated figure. A person
 * may still correct one mistyped from the broker's email: it is its own
 * catalogue field (`cap_rate_stated`, …), editable on the Audit tab, so the
 * unlocked dossier offers it here too (owner, 21 Oct). The calculation is the
 * platform's and never an input. Outside the dossier (the pipeline inspector)
 * there is no edit context and the block reads exactly as it always did.
 */
export function StatedFiguresBlock({
  figures,
  className = '',
}: {
  figures: readonly StatedFigure[] | null | undefined
  className?: string
}) {
  const rows = buildStatedFigureRows(figures)
  if (rows.length === 0) return null
  return (
    <div data-testid="stated-figures" className={`border-t border-white/[0.05] pt-1.5 ${className}`}>
      <div className="mb-1 font-mono text-[8px] uppercase tracking-wider text-stone-500">
        Broker-stated vs calculated
      </div>
      <dl className="space-y-1.5 text-[10px]">
        {rows.map((row) => (
          <div key={row.id} data-stated={row.id} data-status={row.status}>
            <div className="flex justify-between gap-2">
              <dt className="text-stone-500">{row.label}</dt>
              <dd className="font-mono text-stone-200">
                {row.field ? (
                  <EditableValue name={row.field} label={`${row.label} (broker-stated)`} display={row.stated} />
                ) : (
                  row.stated
                )}
              </dd>
            </div>
            <div className="flex justify-between gap-2 text-[9px]">
              <span className="text-stone-600">Platform calculation</span>
              <span className="font-mono text-stone-400">{row.calculated}</span>
            </div>
            {row.variance ? (
              <div className="flex justify-between gap-2 text-[9px]" data-testid={`variance-${row.id}`}>
                <span className="text-amber-300/80">Variance</span>
                <span className="font-mono text-amber-200">{row.variance}</span>
              </div>
            ) : null}
            <p className="text-[8.5px] leading-snug text-stone-600" title={row.basis}>{row.basis}</p>
          </div>
        ))}
      </dl>
    </div>
  )
}
